import { ReactComponent as LogoBlackBlue } from "../../../styles/svg/logoblackblue.svg";
import { ReactComponent as OnTheRight } from "../../../styles/svg/ontheirght.svg";
import { hexToHsl, hexToRgb } from "../../../utils/colorConverters";

// Default logos and side images used when a brand doesn't provide its own
export const defaultLogo = { light: LogoBlackBlue, dark: LogoBlackBlue };
export const defaultSideImage = { light: OnTheRight, dark: OnTheRight };


// Uses a hue-aware threshold because green appears brighter than blue/red
// at the same luminance value.
export const getContrastText = (rgb) => {
    const [rr, gg, bb] = rgb;
    const [r, g, b] = rgb.map(c => {
        c = c / 255;
        return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    });
    const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;

    // Lightness: 0 = black, 1 = white
    const lightness = (Math.max(rr, gg, bb) + Math.min(rr, gg, bb)) / 510;

    // Base threshold per dominant hue
    const max = Math.max(rr, gg, bb);
    let threshold = 0.35;
    if (max === gg && gg > rr && gg > bb) {
    // Green: eyes see green as bright, but mid-tone greens still need white text
        threshold = 0.4; // green
    } else if (max === rr && rr > gg && rr > bb) {
        // Red: moderate
        threshold = 0.3; // red
    } else if (max === bb && bb > rr && bb > gg) {
        // Blue: eyes see blue as dark, needs higher threshold for white text
        threshold = 0.45; // blue
    }

    // Shift threshold based on lightness: dark colors push threshold up (more white text),
    // light colors push threshold down (more black text)
    threshold += (lightness - 0.5) * 0.2;

    return luminance > threshold ? "#000" : "#fff";
};

// Takes the brand hex, extracts the hue, and generates button colors for both modes.
// Follows the same pattern as generateIconColorFromTitle in IconColorGenerator.
// Light mode: hsl(h, 80%, 30%) — saturated and dark, readable with light text
// Dark mode: hsl(h, 70%, 50%) — lighter, still readable with light text on dark bg

export const generateButtonColors = (hex) => {
    const [h] = hexToHsl(hex);
    const light = `hsl(${h}, 80%, 40%)`;
    const dark = `hsl(${h}, 70%, 55%)`;
    const darkText = `hsl(${h}, 80%, 75%)`;
    const lightRgb = hexToRgb(hslToHexHelper(h, 80, 40));
    const darkRgb = hexToRgb(hslToHexHelper(h, 70, 60));
    const darkTextRgb = hexToRgb(hslToHexHelper(h, 80, 75));
    return {
        light,
        dark,
        darkText,
        lightRgb,
        darkRgb,
        darkTextRgb,
        contrastLight: getContrastText(lightRgb),
        contrastDark: getContrastText(darkRgb),
    };
};

// Converts HSL to a 6-char hex for use with hexToRgb
const hslToHexHelper = (h, s, l) => {
    s /= 100;
    l /= 100;
    const c = (1 - Math.abs(2 * l - 1)) * s;
    const x = c * (1 - Math.abs((h / 60) % 2 - 1));
    const m = l - c / 2;
    let r, g, b;
    if (h < 60) { r = c; g = x; b = 0; }
    else if (h < 120) { r = x; g = c; b = 0; }
    else if (h < 180) { r = 0; g = c; b = x; }
    else if (h < 240) { r = 0; g = x; b = c; }
    else if (h < 300) { r = x; g = 0; b = c; }
    else { r = c; g = 0; b = x; }
    const toHex = (v) => Math.round((v + m) * 255).toString(16).padStart(2, '0');
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

// Adds derived color fields (rgb, contrastText, button) to a raw brand object.
// Used both for static lookup and API-fetched brand data.
export const enrichBrand = (brand) => {
    if (!brand) return null;

    const rgb = hexToRgb(brand.color.hex);
    const button = generateButtonColors(brand.color.hex);

    return {
        ...brand,
        color: {
            ...brand.color,
            rgb, // [r, g, b]
            contrastText: button.contrastLight,
            contrastTextRgb: hexToRgb(button.contrastLight),
            button,
        },
    };
};

